/** @file node.rename: renames a node and every reference to it in the puppet and its clips (CR-008). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { unknownNodeWarnings } from '../clip/unknownNodeWarnings';
import { renameClipNodes } from '../library/renameClipNodes';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import { readInput } from '../validate/readInput';
import { refuseAttachmentPrefix } from '../validate/refuseAttachmentPrefix';
import { USER_NAME_PATTERN } from '../validate/userNamePattern';
import { requireNode } from './requireNode';

const NAME_HINT = 'a letter or _, then letters, digits or _ (no . or -, which attachments use), as node.add';

type Rename = (n: string) => string;

/** The node keys (in place, so the order stays), parents and mirror twins. */
function renameNodes(p: Puppet, at: Rename): void {
  p.nodes = Object.fromEntries(Object.entries(p.nodes).map(([name, node]) => [at(name), node]));
  for (const node of Object.values(p.nodes)) {
    if (node.parent !== undefined) node.parent = at(node.parent);
    if (node.mirror !== undefined) node.mirror = at(node.mirror);
  }
}

/** Drawable nodes, impostor anchors and lerps. */
function renameDrawables(p: Puppet, at: Rename): void {
  for (const d of p.drawables) {
    d.node = at(d.node);
    if (d.kind !== 'impostor') continue;
    if (d.anchor?.node !== undefined) d.anchor.node = at(d.anchor.node);
    if (d.lerp !== undefined) d.lerp.node = at(d.lerp.node);
  }
}

/** Attachment host references: `nodeMap` values (source keys name the attached puppet's nodes), `when`, `driver`. */
function renameAttachments(p: Puppet, at: Rename): void {
  for (const a of p.attachments ?? []) {
    for (const [source, host] of Object.entries(a.nodeMap)) a.nodeMap[source] = at(host);
    if (a.when !== undefined) Object.assign(a.when, { hostNode: at(a.when.hostNode), parent: at(a.when.parent) });
    if (a.driver !== undefined) a.driver = at(a.driver);
  }
}

/** The puppet with every node name `from` replaced by `to`; key orders and every other field stay as they are. */
function renameInPuppet(p: Puppet, from: string, to: string): void {
  const at: Rename = (n) => (n === from ? to : n);
  renameNodes(p, at);
  for (const [socket, node] of Object.entries(p.sockets)) p.sockets[socket] = at(node);
  renameDrawables(p, at);
  renameAttachments(p, at);
}

/** Refuses `to` when a clip already names it (a node the puppet lacks): the rename would merge two key sets. */
function refuseClipCollision(draft: PuppetDoc, to: string): void {
  for (const [clipName, clip] of Object.entries(draft.clips)) {
    const hit = unknownNodeWarnings(draft.puppet, clipName, clip).find((w) => w.params?.node === to);
    if (hit !== undefined) {
      throwError(ERROR_CODES.NAME_TAKEN, { node: to, clip: clipName }, {
        path: hit.path ?? `/clips/${clipName}`, hint: `clip ${clipName} already keys a node ${to} the puppet lacks; pick another name or clear those keys`,
      });
    }
  }
}

/**
 * `node.rename { name, to }` → `{ name: to }`: the node key (in place, so the node order stays) and every parent,
 * mirror twin, drawable node, impostor anchor and lerp, socket, attachment host reference (`nodeMap` values, `when`,
 * `driver`) and clip reference (keys, flex parents, events, tracks). `ik` counts ancestors, so chains follow.
 * Flattened `att<i>.` nodes keep their names. Inverse: `node.rename { name: to, to: name }`.
 */
export const nodeRenameCommand: CommandDef<'node.rename'> = {
  name: 'node.rename',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'to']);
    const to = r.raw('to');
    if (typeof to !== 'string' || !USER_NAME_PATTERN.test(to)) failField(r.at('to'), 'a name of letters, digits and _', to, NAME_HINT);
    return { name: r.str('name'), to };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    requireNode(p, input.name, '/input/name');
    refuseAttachmentPrefix(input.name, '/input/name');
    if (input.to === input.name) throwError(ERROR_CODES.VALIDATION, { node: input.name }, { path: '/input/to', hint: 'the node already has this name' });
    if (Object.hasOwn(p.nodes, input.to)) {
      throwError(ERROR_CODES.NAME_TAKEN, { node: input.to }, { path: '/input/to', hint: 'another node has this name; pick another or rename that one first' });
    }
    refuseClipCollision(draft, input.to);
    renameInPuppet(p, input.name, input.to);
    for (const [clipName, clip] of Object.entries(draft.clips)) draft.clips[clipName] = renameClipNodes(clip, { [input.name]: input.to });
    checkPuppet(draft, ctx);
    return { result: { name: input.to }, warnings: [], inverse: [{ name: 'node.rename', input: { name: input.to, to: input.name } }] };
  },
};
